import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  getVelocityClassification, getSkuAnalytics, getTopWriteOffs,
  getWriteOffAlertConfig, putWriteOffAlertConfig, runWriteOffScan,
  getDigestHistory, getStockoutRisk, getRecentPriceChanges,
} from '../lib/api';
import { Link } from 'react-router-dom';
import { ChartLineUp, TrendUp, TrendDown, Package, Skull, ChartBar, Trash, Bell, BellSlash, Broadcast, Clipboard, ArrowRight, ChartLineDown, CurrencyCircleDollar, Lightning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const classColors = {
  A: { bg: 'bg-green-50 border-green-200', chip: 'bg-green-600 text-white', label: 'A · Fast' },
  B: { bg: 'bg-blue-50 border-blue-200', chip: 'bg-brand text-brand-foreground', label: 'B · Medium' },
  C: { bg: 'bg-amber-50 border-amber-200', chip: 'bg-amber-600 text-white', label: 'C · Slow' },
  DEAD: { bg: 'bg-muted border-border', chip: 'bg-slate-800 text-white', label: 'DEAD · No sales' },
};

export default function Analytics() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  const [data, setData] = useState({ summary: {}, skus: [] });
  const [loading, setLoading] = useState(true);
  const [lookback, setLookback] = useState(90);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [writeOffs, setWriteOffs] = useState({ items: [], total_units: 0, total_value_lost: 0, total_events: 0 });
  const [writeOffLookback, setWriteOffLookback] = useState(30);
  const [writeOffLoading, setWriteOffLoading] = useState(true);
  const [alertCfg, setAlertCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [digests, setDigests] = useState([]);
  const [digestsLoading, setDigestsLoading] = useState(true);

  // New "Operations at a glance" panels — surface the riskiest SKUs and the
  // most recent price edits at the top of the page so managers don't have
  // to bounce across multiple tools to triage day-to-day.
  const [stockoutRisk, setStockoutRisk] = useState({ rows: [] });
  const [stockoutLoading, setStockoutLoading] = useState(true);
  const [priceChanges, setPriceChanges] = useState([]);
  const [priceChangesLoading, setPriceChangesLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getVelocityClassification(lookback);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchWriteOffs = async () => {
    setWriteOffLoading(true);
    try {
      const res = await getTopWriteOffs(writeOffLookback, 10);
      setWriteOffs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setWriteOffLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [lookback]);
  useEffect(() => { fetchWriteOffs(); }, [writeOffLookback]);
  useEffect(() => {
    if (!isManager) return;
    getWriteOffAlertConfig().then(r => setAlertCfg(r.data)).catch(() => {});
  }, [isManager]);
  useEffect(() => {
    setDigestsLoading(true);
    getDigestHistory(8)
      .then(r => setDigests(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDigests([]))
      .finally(() => setDigestsLoading(false));
  }, []);

  // Operations panels — fetch in parallel on mount, no refresh knobs needed
  // (the dashboard auto-refreshes whenever the user navigates back to it).
  useEffect(() => {
    setStockoutLoading(true);
    getStockoutRisk({ lead_time_days: 7 })
      .then(r => setStockoutRisk(r.data || { rows: [] }))
      .catch(() => setStockoutRisk({ rows: [] }))
      .finally(() => setStockoutLoading(false));

    setPriceChangesLoading(true);
    getRecentPriceChanges(15)
      .then(r => setPriceChanges(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPriceChanges([]))
      .finally(() => setPriceChangesLoading(false));
  }, []);

  const saveAlertCfg = async (next) => {
    if (!isAdmin) return;
    setSavingCfg(true);
    try {
      const res = await putWriteOffAlertConfig(next);
      setAlertCfg(res.data);
      toast.success(next.enabled ? 'Shrinkage alerts enabled' : 'Shrinkage alert settings saved');
    } catch (e) {
      toast.error('Failed to save alert settings');
    } finally {
      setSavingCfg(false);
    }
  };

  const scanNow = async () => {
    if (!isAdmin) return;
    setScanning(true);
    try {
      // Persist current config first so the scan uses the latest threshold
      // even if the user edited it and clicked Scan before blur fired.
      if (alertCfg) {
        try {
          const saved = await putWriteOffAlertConfig(alertCfg);
          setAlertCfg(saved.data);
        } catch (_) { /* non-blocking */ }
      }
      const res = await runWriteOffScan();
      if (res.data?.skipped === 'disabled') {
        toast('Alerts disabled — enable them first');
      } else {
        toast.success(`Scan complete · ${res.data?.fired || 0} fired / ${res.data?.checked || 0} checked`);
      }
      fetchWriteOffs();
    } catch (e) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const openDetail = async (sku) => {
    setDetail({ loading: true, sku_code: sku.sku_code, name: sku.name });
    setDetailLoading(true);
    try {
      const res = await getSkuAnalytics(sku.sku_id, 90);
      setDetail(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const filtered = filter
    ? data.skus.filter(s => s.velocity_class === filter)
    : data.skus;

  return (
    <Layout>
      <div data-testid="analytics-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <ChartBar size={32} weight="fill" className="text-brand" />
              {t('analytics.page.title')}
            </h1>
            <p className="text-muted-foreground">{t('analytics.page.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="overline">{t('analytics.filters.window')}</label>
            <select
              value={lookback}
              onChange={(e) => setLookback(parseInt(e.target.value))}
              className="px-3 py-2 border border-border font-medium"
              data-testid="analytics-window-select"
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
            </select>
          </div>
        </div>

        {/* Operations at a glance — Stockout risk + Recent price changes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Stockout risk */}
          <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="ops-stockout-card">
            <div className="flex items-center justify-between p-3 border-b border-border bg-red-50/30">
              <div className="flex items-center gap-3">
                <div className="bg-red-50 border border-red-200 p-2">
                  <ChartLineDown size={18} weight="fill" className="text-red-600" />
                </div>
                <div>
                  <p className="overline">Risk</p>
                  <h3 className="text-base font-bold tracking-tight">Stockout risk · 14d window</h3>
                </div>
              </div>
              <Link to="/reorder-suggestions" className="text-xs font-bold uppercase tracking-wider text-brand hover:underline" data-testid="goto-reorder">
                Draft POs <ArrowRight size={11} className="inline" />
              </Link>
            </div>
            {stockoutLoading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : stockoutRisk.rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Lightning size={28} weight="duotone" className="mx-auto mb-2 text-emerald-500" />
                Every SKU has comfortable cover at the current burn rate.
              </div>
            ) : (
              <table className="w-full table-dense">
                <thead>
                  <tr><th>SKU</th><th className="text-right">Days Left</th><th>Risk</th></tr>
                </thead>
                <tbody>
                  {stockoutRisk.rows.slice(0, 5).map((r) => (
                    <tr key={r.sku_id} data-testid={`stockout-row-${r.sku_code}`}>
                      <td>
                        <div className="font-mono font-bold text-sm">{r.sku_code}</div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">{r.name}</div>
                      </td>
                      <td className="font-mono text-right">
                        <span className="font-bold">{r.days_left}d</span>
                        <div className="text-[10px] text-muted-foreground">{r.projected_stockout_date}</div>
                      </td>
                      <td>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          r.risk_level === 'high' ? 'bg-red-100 text-red-800' :
                          r.risk_level === 'medium' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {r.risk_level}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {stockoutRisk.rows.length > 5 && (
              <div className="p-2 text-center text-[11px] text-muted-foreground border-t border-border">
                +{stockoutRisk.rows.length - 5} more — see <Link to="/reorder-suggestions" className="text-brand font-bold">Reorder Suggestions</Link>
              </div>
            )}
          </div>

          {/* Recent price changes */}
          <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="ops-price-changes-card">
            <div className="flex items-center justify-between p-3 border-b border-border bg-blue-50/30">
              <div className="flex items-center gap-3">
                <div className="bg-blue-50 border border-blue-200 p-2">
                  <CurrencyCircleDollar size={18} weight="fill" className="text-brand" />
                </div>
                <div>
                  <p className="overline">Pricing</p>
                  <h3 className="text-base font-bold tracking-tight">Recent price changes</h3>
                </div>
              </div>
              <Link to="/inventory" className="text-xs font-bold uppercase tracking-wider text-brand hover:underline">
                Inventory <ArrowRight size={11} className="inline" />
              </Link>
            </div>
            {priceChangesLoading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : priceChanges.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No price changes logged yet — bulk edits and inline edits both record here.
              </div>
            ) : (
              <table className="w-full table-dense">
                <thead>
                  <tr><th>SKU</th><th className="text-right">Δ</th><th className="text-right">When</th></tr>
                </thead>
                <tbody>
                  {priceChanges.slice(0, 8).map((p, i) => {
                    const up = (p.delta || 0) > 0;
                    const dn = (p.delta || 0) < 0;
                    return (
                      <tr key={i} data-testid={`price-change-row-${p.sku_code || i}`}>
                        <td>
                          <div className="font-mono font-bold text-sm">{p.sku_code || '—'}</div>
                          <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{p.sku_name}</div>
                        </td>
                        <td className="font-mono text-right">
                          <div className={`font-bold ${up ? 'text-red-700' : dn ? 'text-emerald-700' : ''}`}>
                            {up ? '↑' : dn ? '↓' : ''} ${Math.abs(p.delta || 0).toFixed(2)}
                          </div>
                          {p.delta_pct != null && (
                            <div className="text-[10px] text-muted-foreground">
                              ${(p.prev_price || 0).toFixed(2)} → ${(p.new_price || 0).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td className="text-right text-[11px] text-muted-foreground">
                          {p.changed_at ? new Date(p.changed_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Summary ABCD cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {['A', 'B', 'C', 'DEAD'].map((c) => {
            const { bg, chip, label } = classColors[c];
            const count = data.summary?.[c] || 0;
            const Icon = c === 'A' ? TrendUp : c === 'DEAD' ? Skull : Package;
            return (
              <button
                key={c}
                onClick={() => setFilter(filter === c ? '' : c)}
                data-testid={`velocity-card-${c}`}
                className={`${bg} border p-4 text-left transition hover:-translate-y-0.5 ${filter === c ? 'ring-2 ring-brand' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`${chip} px-2 py-0.5 text-xs font-bold uppercase tracking-widest`}>
                    {label}
                  </span>
                  <Icon size={20} weight="bold" className="opacity-60" />
                </div>
                <p className="text-4xl font-bold">{count}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold mt-1">
                  {data.summary?.total ? `${Math.round((count / data.summary.total) * 100)}%` : '0%'} of catalog
                </p>
              </button>
            );
          })}
        </div>

        {filter && (
          <div className="mb-3">
            <button
              onClick={() => setFilter('')}
              data-testid="clear-filter"
              className="text-sm text-brand font-bold uppercase"
            >
              × Clear filter · showing {filtered.length} of {data.skus.length} SKUs
            </button>
          </div>
        )}

        {/* Top written-off SKUs */}
        <div data-testid="top-write-offs-widget" className="bg-card border border-border rounded-lg mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="bg-red-50 border border-red-200 p-2">
                <Trash size={20} weight="fill" className="text-red-600" />
              </div>
              <div>
                <p className="overline">Shrinkage</p>
                <h2 className="text-lg font-bold tracking-tight">
                  Top Written-Off SKUs
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-baseline gap-4">
                <div>
                  <p className="overline">Units Lost</p>
                  <p className="text-2xl font-bold font-mono" data-testid="write-off-total-units">
                    {writeOffs.total_units || 0}
                  </p>
                </div>
                <div>
                  <p className="overline">Value Lost</p>
                  <p className="text-2xl font-bold font-mono text-red-600" data-testid="write-off-total-value">
                    ${(writeOffs.total_value_lost || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="overline">Events</p>
                  <p className="text-2xl font-bold font-mono" data-testid="write-off-total-events">
                    {writeOffs.total_events || 0}
                  </p>
                </div>
              </div>
              <div className="flex bg-muted border border-border">
                {[30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setWriteOffLookback(d)}
                    data-testid={`write-off-window-${d}`}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition ${
                      writeOffLookback === d
                        ? 'bg-brand text-white'
                        : 'text-muted-foreground hover:text-brand'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Admin threshold-alert config bar */}
          {isManager && alertCfg && (
            <div
              data-testid="write-off-alert-config"
              className={`flex flex-col md:flex-row md:items-center gap-3 px-4 py-2.5 text-xs border-b border-border ${
                alertCfg.enabled ? 'bg-amber-50' : 'bg-muted'
              }`}
            >
              <div className="flex items-center gap-2 font-bold uppercase tracking-widest">
                {alertCfg.enabled
                  ? <Bell size={14} weight="fill" className="text-amber-600" />
                  : <BellSlash size={14} weight="bold" className="text-muted-foreground" />
                }
                Slack alert {alertCfg.enabled ? 'ON' : 'OFF'}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-muted-foreground">Threshold $</label>
                <input
                  type="number"
                  min="0"
                  step="25"
                  disabled={!isAdmin || savingCfg}
                  value={alertCfg.threshold_usd}
                  onChange={(e) => setAlertCfg({ ...alertCfg, threshold_usd: parseFloat(e.target.value) || 0 })}
                  onBlur={() => isAdmin && saveAlertCfg(alertCfg)}
                  className="w-24 px-2 py-1 border border-border font-mono bg-card disabled:opacity-60"
                  data-testid="write-off-alert-threshold"
                />
                <label className="text-muted-foreground">over</label>
                <select
                  disabled={!isAdmin || savingCfg}
                  value={alertCfg.lookback_days}
                  onChange={(e) => {
                    const next = { ...alertCfg, lookback_days: parseInt(e.target.value) };
                    setAlertCfg(next);
                    if (isAdmin) saveAlertCfg(next);
                  }}
                  className="px-2 py-1 border border-border bg-card font-bold disabled:opacity-60"
                  data-testid="write-off-alert-window"
                >
                  <option value={7}>7d</option>
                  <option value={30}>30d</option>
                  <option value={90}>90d</option>
                </select>
                <label className="text-muted-foreground">cooldown</label>
                <select
                  disabled={!isAdmin || savingCfg}
                  value={alertCfg.cooldown_hours}
                  onChange={(e) => {
                    const next = { ...alertCfg, cooldown_hours: parseInt(e.target.value) };
                    setAlertCfg(next);
                    if (isAdmin) saveAlertCfg(next);
                  }}
                  className="px-2 py-1 border border-border bg-card font-bold disabled:opacity-60"
                  data-testid="write-off-alert-cooldown"
                >
                  <option value={6}>6h</option>
                  <option value={24}>24h</option>
                  <option value={72}>3d</option>
                  <option value={168}>7d</option>
                </select>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 md:ml-auto">
                  <button
                    onClick={() => saveAlertCfg({ ...alertCfg, enabled: !alertCfg.enabled })}
                    disabled={savingCfg}
                    data-testid="write-off-alert-toggle"
                    className={`px-3 py-1 font-bold uppercase tracking-widest border transition ${
                      alertCfg.enabled
                        ? 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700'
                        : 'bg-card text-foreground border-border hover:border-brand hover:text-brand'
                    }`}
                  >
                    {alertCfg.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={scanNow}
                    disabled={scanning}
                    data-testid="write-off-alert-scan"
                    className="px-3 py-1 font-bold uppercase tracking-widest border border-border bg-card hover:border-brand hover:text-brand disabled:opacity-50 transition"
                  >
                    {scanning ? 'Scanning…' : 'Scan now'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th className="w-12">#</th>
                  <th>SKU</th>
                  <th>Name</th>
                  <th className="text-right">Units</th>
                  <th className="text-right">Unit Cost</th>
                  <th className="text-right">$ Lost</th>
                  <th className="text-right">Events</th>
                  <th>Last Write-Off</th>
                </tr>
              </thead>
              <tbody>
                {writeOffLoading ? (
                  <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : (writeOffs.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="write-off-empty">
                      No write-offs in the last {writeOffLookback} days · clean inventory.
                    </td>
                  </tr>
                ) : (writeOffs.items || []).map((it, i) => {
                  const overThreshold = alertCfg?.enabled && (it.value_lost || 0) >= (alertCfg?.threshold_usd || 0);
                  return (
                    <tr
                      key={it.variant_id}
                      data-testid={`write-off-row-${it.sku_code || it.variant_id}`}
                      className={overThreshold ? 'bg-amber-50' : ''}
                    >
                      <td className="font-mono font-bold text-muted-foreground">#{i + 1}</td>
                      <td className="font-mono font-semibold text-brand">
                        {it.sku_code || '—'}
                        {overThreshold && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                            <Bell size={10} weight="fill" /> alert
                          </span>
                        )}
                      </td>
                      <td>{it.sku_name || it.variant_name || '—'}</td>
                      <td className="font-mono text-right font-bold">{it.units_written_off}</td>
                      <td className="font-mono text-right text-muted-foreground">${(it.unit_price || 0).toFixed(2)}</td>
                      <td className="font-mono text-right font-bold text-red-600">${(it.value_lost || 0).toFixed(2)}</td>
                      <td className="font-mono text-right text-muted-foreground">{it.events}</td>
                      <td className="font-mono text-xs text-muted-foreground">
                        {it.last_written_off_at ? new Date(it.last_written_off_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Digest history — weekly Inbound Ops executive broadcast */}
        <div data-testid="digest-history-widget" className="bg-card border border-border rounded-lg mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 border border-blue-200 p-2">
                <Broadcast size={20} weight="fill" className="text-brand" />
              </div>
              <div>
                <p className="overline">Executive Broadcast</p>
                <h2 className="text-lg font-bold tracking-tight">
                  Weekly Inbound Ops · Digest History
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              <Clipboard size={14} weight="bold" />
              <span data-testid="digest-history-count">{digests.length} published</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th className="w-12">#</th>
                  <th>Published</th>
                  <th>By</th>
                  <th className="text-right">Suppliers</th>
                  <th className="text-right">Receivers</th>
                  <th className="text-right">Write-offs</th>
                  <th className="text-right">$ Lost</th>
                </tr>
              </thead>
              <tbody>
                {digestsLoading ? (
                  <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : digests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="digest-history-empty">
                      No digests published yet · scheduler runs every Monday 08:00 UTC.
                    </td>
                  </tr>
                ) : digests.map((d, i) => (
                  <tr key={d.id || i} data-testid={`digest-row-${i}`}>
                    <td className="font-mono font-bold text-muted-foreground">#{i + 1}</td>
                    <td className="font-mono text-sm">
                      {d.published_at ? new Date(d.published_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td>
                      <span className="text-xs uppercase font-bold tracking-widest text-muted-foreground">{d.published_by || 'scheduler'}</span>
                    </td>
                    <td className="font-mono text-right font-bold">{d.supplier_count ?? 0}</td>
                    <td className="font-mono text-right font-bold">{d.receiver_count ?? 0}</td>
                    <td className="font-mono text-right font-bold">{d.writeoff_count ?? 0}</td>
                    <td className="font-mono text-right font-bold text-red-600">
                      ${Number(d.writeoff_value_lost || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="w-16">Rank</th>
                <th>SKU</th>
                <th>Name</th>
                <th>Velocity</th>
                <th>Consumption ({lookback}d)</th>
                <th>% of Total</th>
                <th>Current Stock</th>
              </tr>
            </thead>            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No SKUs match filter</td></tr>
              ) : filtered.slice(0, 500).map(sku => (
                <tr
                  key={sku.sku_id}
                  onClick={() => openDetail(sku)}
                  className="cursor-pointer hover:bg-muted"
                  data-testid={`analytics-row-${sku.sku_code}`}
                >
                  <td className="font-mono font-bold text-muted-foreground">#{sku.rank}</td>
                  <td className="font-mono font-semibold text-brand">{sku.sku_code}</td>
                  <td>{sku.name}</td>
                  <td>
                    <span className={`${classColors[sku.velocity_class].chip} px-2 py-0.5 text-xs font-bold uppercase`}>
                      {sku.velocity_class}
                    </span>
                  </td>
                  <td className="font-mono font-bold">{sku.consumption}</td>
                  <td className="font-mono">{sku.consumption_pct}%</td>
                  <td className="font-mono text-muted-foreground">{sku.current_stock}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail dialog */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="border border-border rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sku-detail-dialog">
            <DialogHeader>
              <DialogTitle>
                {detail?.sku?.sku_code || detail?.sku_code} · {detail?.sku?.name || detail?.name}
              </DialogTitle>
            </DialogHeader>
            {detailLoading || detail?.loading ? (
              <div className="py-12 text-center text-muted-foreground">Loading analytics...</div>
            ) : detail ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-muted border border-border p-3">
                    <p className="overline">Avg Daily</p>
                    <p className="text-2xl font-bold">{detail.avg_daily_consumption}</p>
                  </div>
                  <div className="bg-muted border border-border p-3">
                    <p className="overline">Total ({detail.lookback_days}d)</p>
                    <p className="text-2xl font-bold">{detail.total_consumption}</p>
                  </div>
                  <div className="bg-muted border border-border p-3">
                    <p className="overline">Current Stock</p>
                    <p className="text-2xl font-bold">{detail.current_stock_total}</p>
                  </div>
                  <div className={`${detail.trend === 'up' ? 'bg-green-50 border-green-200' : detail.trend === 'down' ? 'bg-red-50 border-red-200' : 'bg-muted border-border'} border p-3`}>
                    <p className="overline">Trend</p>
                    <p className="text-2xl font-bold flex items-center gap-1">
                      {detail.trend === 'up' ? <TrendUp size={20} weight="bold" className="text-green-600" /> : detail.trend === 'down' ? <TrendDown size={20} weight="bold" className="text-red-600" /> : null}
                      {detail.trend_pct > 0 ? '+' : ''}{detail.trend_pct}%
                    </p>
                  </div>
                </div>

                {detail.daily_consumption?.length > 0 && (
                  <div>
                    <p className="overline mb-2">Daily Consumption</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={detail.daily_consumption}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" style={{ fontSize: 10 }} />
                        <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: 6 }} />
                        <Line type="monotone" dataKey="consumption" stroke="hsl(var(--brand))" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {detail.current_stock_by_warehouse?.length > 0 && (
                  <div>
                    <p className="overline mb-2">Stock by Warehouse</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {detail.current_stock_by_warehouse.map(w => (
                        <div key={w.warehouse_id} className="border border-border p-3 flex justify-between">
                          <span className="font-mono font-bold">{w.warehouse_code}</span>
                          <span className="font-mono">{w.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
